import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Skeleton, Empty, Tooltip, Drawer } from "antd";
import {
  LockOutlined, UserOutlined, EyeOutlined, PlusSquareOutlined,
  EditOutlined, DeleteOutlined, SwapOutlined, CheckCircleFilled,
  CloseCircleFilled, ReloadOutlined, SafetyCertificateOutlined, PlusOutlined,
} from "@ant-design/icons";
import { authHeaders } from "../utils/session";

/* ── Permission config ───────────────────────────────────────────────────── */
const PERMS = [
  { key: "can_view",      label: "View",   icon: <EyeOutlined />,         color: "#2563eb", bg: "#eff6ff",  light: "#dbeafe" },
  { key: "can_create",    label: "Add",    icon: <PlusSquareOutlined />,  color: "#16a34a", bg: "#f0fdf4",  light: "#bbf7d0" },
  { key: "can_edit",      label: "Edit",   icon: <EditOutlined />,        color: "#d97706", bg: "#fffbeb",  light: "#fde68a" },
  { key: "can_delete",    label: "Delete", icon: <DeleteOutlined />,      color: "#dc2626", bg: "#fef2f2",  light: "#fecaca" },
  { key: "import_export", label: "I/E",    icon: <SwapOutlined />,        color: "#7c3aed", bg: "#f5f3ff",  light: "#ddd6fe" },
];

const PERM_COL_WIDTH = "56px";
const gridCols = `1fr repeat(${PERMS.length}, ${PERM_COL_WIDTH})`;

const ROLE_COLORS = ["#5b48e8","#0284c7","#16a34a","#d97706","#dc2626","#7c3aed","#0891b2","#be185d"];
const roleColor = (name = "") => { let h = 0; for (const c of name) h = (h + c.charCodeAt(0)) % ROLE_COLORS.length; return ROLE_COLORS[h]; };
const initials = (name = "") => name.trim().split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2) || "R";
const authH = () => ({ ...authHeaders() });

/* ── Permission Dot ──────────────────────────────────────────────────────── */
const PermDot = ({ granted, perm }) => (
  <Tooltip title={`${perm.label}: ${granted ? "Granted" : "Not granted"}`}>
    <div style={{
      width: "28px", height: "28px", borderRadius: "7px",
      background: granted ? perm.bg : "#f9fafb",
      border: `1.5px solid ${granted ? perm.light : "#e5e7eb"}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: "12px", color: granted ? perm.color : "#d1d5db",
      transition: "all 0.15s",
    }}>
      {granted ? <CheckCircleFilled /> : <CloseCircleFilled style={{ opacity: 0.4 }} />}
    </div>
  </Tooltip>
);

/* ── Role Selector Card ──────────────────────────────────────────────────── */
const RoleCard = ({ role, selected, onClick }) => {
  const color = roleColor(role.role_name);
  return (
    <div
      onClick={onClick}
      style={{
        background: selected ? `linear-gradient(135deg, ${color}18, ${color}08)` : "white",
        border: `2px solid ${selected ? color : "#e5e7eb"}`,
        borderRadius: "12px", padding: "16px 14px", cursor: "pointer",
        transition: "all 0.2s", width: "130px", textAlign: "center",
        boxShadow: selected ? `0 4px 12px ${color}30` : "0 1px 3px rgba(0,0,0,0.05)",
        transform: selected ? "translateY(-2px)" : "none",
        flexShrink: 0,
      }}
    >
      <div style={{
        width: "40px", height: "40px", borderRadius: "10px", margin: "0 auto 8px",
        background: selected ? `linear-gradient(135deg, ${color}, ${color}99)` : "#f3f4f6",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "14px", fontWeight: 700, color: selected ? "white" : "#9ca3af",
      }}>
        {initials(role.role_name)}
      </div>
      <div style={{ fontSize: "12.5px", fontWeight: selected ? 700 : 600, color: selected ? color : "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {role.role_name}
      </div>
      {selected && (
        <div style={{ marginTop: "6px" }}>
          <span style={{ fontSize: "10px", background: color, color: "white", padding: "2px 8px", borderRadius: "10px", fontWeight: 700 }}>Active</span>
        </div>
      )}
    </div>
  );
};

/* ── Permission Matrix Row ───────────────────────────────────────────────── */
const MatrixRow = ({ name, icon, perms, depth = 0, isSubmenu }) => {
  const hasAny = PERMS.some(p => perms[p.key] === 1);
  const defaultBg = hasAny ? (isSubmenu ? "rgba(91,72,232,0.02)" : "rgba(91,72,232,0.04)") : "transparent";

  return (
    <div
      style={{ display: "grid", gridTemplateColumns: gridCols, alignItems: "center", padding: `10px 16px 10px ${16 + depth * 24}px`, background: defaultBg, borderBottom: "1px solid #f3f4f6", transition: "background 0.15s" }}
      onMouseEnter={e => { e.currentTarget.style.background = "rgba(91,72,232,0.06)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = defaultBg; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
        {depth > 0 && (
          <div style={{ width: "12px", height: "12px", borderLeft: "2px solid #e5e7eb", borderBottom: "2px solid #e5e7eb", borderRadius: "0 0 0 4px", marginRight: "2px", flexShrink: 0 }} />
        )}
        {icon && <i className={icon} style={{ color: hasAny ? "#5b48e8" : "#d1d5db", fontSize: "13px", width: "16px", flexShrink: 0 }} />}
        <span style={{ fontSize: isSubmenu ? "13px" : "13.5px", fontWeight: isSubmenu ? 400 : 600, color: hasAny ? "#111827" : "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {name}
        </span>
        {hasAny && (
          <span style={{ fontSize: "10px", background: "#f5f3ff", color: "#5b48e8", padding: "1px 6px", borderRadius: "8px", fontWeight: 700, flexShrink: 0 }}>
            {PERMS.filter(p => perms[p.key] === 1).length}/{PERMS.length}
          </span>
        )}
      </div>
      {PERMS.map(p => (
        <div key={p.key} style={{ display: "flex", justifyContent: "center" }}>
          <PermDot granted={perms[p.key] === 1} perm={p} />
        </div>
      ))}
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────────────────────── */
const ManagePermissions = () => {
  // ── Role matrix state ────────────────────────────────────────────────────
  const [roles, setRoles]               = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [matrixData, setMatrixData]     = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);

  // ── CMS Permissions CRUD state ───────────────────────────────────────────
  const [permList, setPermList]         = useState([]);
  const [permLoading, setPermLoading]   = useState(false);
  const [permDrawerOpen, setPermDrawerOpen] = useState(false);
  const [permEditItem, setPermEditItem] = useState(null);
  const [permSaving, setPermSaving]     = useState(false);
  const [permForm, setPermForm]         = useState({ permission_name: "", module: "", description: "" });
  const [permFormErr, setPermFormErr]   = useState("");

  // ── Fetch roles: GET /api/cms-roles ──────────────────────────────────────
  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/cms-roles`, { headers: authH() });
      const list = res.data.data || [];
      setRoles(list);
      if (list.length > 0 && !selectedRole) {
        setSelectedRole(list[0]);
        fetchMatrix(list[0].id);
      }
    } catch { toast.error("Failed to load roles"); }
    finally { setRolesLoading(false); }
  }, []);

  // ── Fetch CMS permissions: GET /api/cms-permissions ──────────────────────
  const fetchPermissions = useCallback(async () => {
    setPermLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/cms-permissions`, { headers: authH() });
      setPermList(res.data.data || []);
    } catch { toast.error("Failed to load permissions"); }
    finally { setPermLoading(false); }
  }, []);

  useEffect(() => { fetchRoles(); fetchPermissions(); }, [fetchRoles, fetchPermissions]);

  // ── Fetch role menu access matrix ────────────────────────────────────────
  // New API: GET /api/cms-menus + GET /api/cms-roles/{id}/menus
  // Permissions are flat on menu_access item (was nested in menu_permition[0])
  // Menus use `id` (was `menu_id`), sub-menus use `id` (was `sub_menu_id`)
  const fetchMatrix = async (roleId) => {
    setMatrixLoading(true);
    setMatrixData(null);
    try {
      const [menuRes, roleRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/cms-menus`, { headers: authH() }),
        axios.get(`${API_BASE_URL}/cms-roles/${roleId}/menus`, { headers: authH() }),
      ]);
      const menus = menuRes.data.data || [];
      const det   = roleRes.data.data;

      const emptyPerms = () => { const p = {}; PERMS.forEach(x => { p[x.key] = 0; }); return p; };

      // Build granted map keyed by cms_menu_id / cms_sub_menu_id (new API)
      const granted = {};
      (det?.menu_access || []).forEach(m => {
        const p = emptyPerms();
        PERMS.forEach(x => { p[x.key] = m[x.key] ?? 0; });
        granted[m.cms_menu_id] = { perms: p, subs: {} };
        (m.sub_menus || []).forEach(sm => {
          const sp = emptyPerms();
          PERMS.forEach(x => { sp[x.key] = sm[x.key] ?? 0; });
          granted[m.cms_menu_id].subs[sm.cms_sub_menu_id] = sp;
        });
      });

      // Build rows using menu `id` (new API, was menu_id)
      const rows = menus.map(menu => ({
        id: menu.id,
        name: menu.menu_name,
        icon: menu.menu_icon,
        perms: granted[menu.id]?.perms || emptyPerms(),
        subMenus: (menu.sub_menus || []).map(sm => ({
          id: sm.id,
          name: sm.sub_menu_name,
          icon: sm.sub_menu_icon,
          perms: granted[menu.id]?.subs[sm.id] || emptyPerms(),
        })),
      }));

      setMatrixData({ rows, role: det?.role });
    } catch { toast.error("Failed to load permissions"); }
    finally { setMatrixLoading(false); }
  };

  const handleSelectRole = (role) => {
    setSelectedRole(role);
    fetchMatrix(role.id);
  };

  // ── CMS Permissions CRUD ─────────────────────────────────────────────────

  const openAddPerm = () => {
    setPermEditItem(null);
    setPermForm({ permission_name: "", module: "", description: "" });
    setPermFormErr("");
    setPermDrawerOpen(true);
  };

  const openEditPerm = (item) => {
    setPermEditItem(item);
    setPermForm({
      permission_name: item.permission_name,
      module: item.module || "",
      description: item.description || "",
    });
    setPermFormErr("");
    setPermDrawerOpen(true);
  };

  // POST /api/cms-permissions | PUT /api/cms-permissions/{id}
  const handleSavePerm = async () => {
    if (!permForm.permission_name.trim()) { setPermFormErr("Permission name is required"); return; }
    setPermFormErr("");
    setPermSaving(true);
    try {
      if (permEditItem) {
        await axios.put(`${API_BASE_URL}/cms-permissions/${permEditItem.id}`, permForm, { headers: { "Content-Type": "application/json", ...authH() } });
        toast.success("Permission updated");
      } else {
        await axios.post(`${API_BASE_URL}/cms-permissions`, permForm, { headers: { "Content-Type": "application/json", ...authH() } });
        toast.success("Permission created");
      }
      setPermDrawerOpen(false);
      fetchPermissions();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save permission");
    } finally { setPermSaving(false); }
  };

  // DELETE /api/cms-permissions/{id}
  const handleDeletePerm = async (id, name) => {
    const result = await Swal.fire({
      title: `Delete "${name}"?`, text: "This removes it from all role mappings.", icon: "warning",
      showCancelButton: true, confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete", cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${API_BASE_URL}/cms-permissions/${id}`, { headers: authH() });
      toast.success("Permission deleted");
      fetchPermissions();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete permission");
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const grantedMenus  = matrixData?.rows.filter(r => PERMS.some(p => r.perms[p.key] === 1)).length || 0;
  const totalMenus    = matrixData?.rows.length || 0;
  const totalGranted  = matrixData?.rows.reduce((acc, r) => acc + PERMS.filter(p => r.perms[p.key] === 1).length, 0) || 0;

  // Group permissions by module for display
  const permByModule = permList.reduce((acc, p) => {
    const m = p.module || "general";
    if (!acc[m]) acc[m] = [];
    acc[m].push(p);
    return acc;
  }, {});

  const MODULE_COLORS = { leads: "#2563eb", orders: "#d97706", users: "#16a34a", content: "#7c3aed", dashboard: "#0891b2", email: "#be185d", general: "#6b7280" };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Page Header ── */}
          <div style={{
            background: "linear-gradient(135deg,#0284c7 0%,#0891b2 60%,#0e7490 100%)",
            borderRadius: "16px", padding: "28px 32px", marginBottom: "28px",
            boxShadow: "0 8px 32px rgba(2,132,199,0.28)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexWrap: "wrap", gap: "16px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ width: "56px", height: "56px", background: "rgba(255,255,255,.22)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", color: "white" }}>
                <LockOutlined />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: "24px", fontWeight: 700 }}>Manage Permissions</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: "14px" }}>Configure CMS permissions and audit role-based access</p>
              </div>
            </div>
            <div style={{ background: "rgba(255,255,255,.18)", padding: "10px 18px", borderRadius: "10px", display: "flex", gap: "6px", alignItems: "center", fontSize: "13px", color: "white" }}>
              <span>CMS</span>
              <span style={{ color: "rgba(255,255,255,.5)" }}>›</span>
              <span style={{ fontWeight: 600 }}>Manage Permissions</span>
            </div>
          </div>

          {/* ── CMS Permissions CRUD ── */}
          <div style={{ background: "white", borderRadius: "16px", marginBottom: "24px", border: "1px solid #f0f0f0", boxShadow: "0 2px 16px rgba(0,0,0,.07)", overflow: "hidden" }}>
            {/* Section header */}
            <div style={{ padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #f0f0f0", background: "#fafafa" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "34px", height: "34px", background: "#f0fdf4", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a", fontSize: "15px" }}>
                  <SafetyCertificateOutlined />
                </div>
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 700, color: "#111827" }}>CMS Permissions</div>
                  <div style={{ fontSize: "12px", color: "#9ca3af" }}>{permList.length} permission{permList.length !== 1 ? "s" : ""} configured</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={fetchPermissions} style={{ width: "34px", height: "34px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: "14px" }}>
                  <ReloadOutlined />
                </button>
                <button onClick={openAddPerm} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 14px", height: "34px", borderRadius: "8px", border: "none", background: "#16a34a", color: "white", fontWeight: 600, fontSize: "13px", cursor: "pointer" }}>
                  <PlusOutlined /> Add Permission
                </button>
              </div>
            </div>

            {/* Permissions list grouped by module */}
            {permLoading ? (
              <div style={{ padding: "20px 24px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {[1,2,3,4,5,6].map(i => <Skeleton.Button key={i} active style={{ width: "140px", height: "32px", borderRadius: "8px" }} />)}
              </div>
            ) : permList.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center" }}>
                <Empty description={<span style={{ color: "#9ca3af" }}>No permissions yet. Create one to get started.</span>} />
              </div>
            ) : (
              <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: "14px" }}>
                {Object.entries(permByModule).map(([module, perms]) => {
                  const modColor = MODULE_COLORS[module] || "#6b7280";
                  return (
                    <div key={module}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: modColor, display: "inline-block" }} />
                        <span style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.8px" }}>{module}</span>
                        <span style={{ fontSize: "11px", color: "#d1d5db" }}>({perms.length})</span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {perms.map(p => (
                          <div key={p.id} style={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            padding: "5px 8px 5px 10px", borderRadius: "8px",
                            background: "#f9fafb", border: "1px solid #e5e7eb",
                            fontSize: "12.5px", color: "#374151", fontWeight: 500,
                          }}>
                            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: p.is_active === 1 ? "#22c55e" : "#d1d5db", flexShrink: 0 }} />
                            {p.permission_name}
                            <span style={{ fontSize: "10px", background: "#f0f0f0", color: "#9ca3af", padding: "1px 5px", borderRadius: "5px", fontFamily: "monospace" }}>{p.slug}</span>
                            <div style={{ display: "flex", gap: "3px", marginLeft: "2px" }}>
                              <Tooltip title="Edit">
                                <button onClick={() => openEditPerm(p)} style={{ width: "22px", height: "22px", borderRadius: "5px", border: "1px solid #e0eaff", background: "#f0f5ff", color: "#2563eb", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px" }}>
                                  <EditOutlined />
                                </button>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <button onClick={() => handleDeletePerm(p.id, p.permission_name)} style={{ width: "22px", height: "22px", borderRadius: "5px", border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px" }}>
                                  <DeleteOutlined />
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Role Selector ── */}
          <div style={{ background: "white", borderRadius: "16px", padding: "20px 24px", marginBottom: "24px", border: "1px solid #f0f0f0", boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "14px" }}>
              <UserOutlined style={{ marginRight: "6px" }} /> Select Role to View Permissions
            </div>
            {rolesLoading ? (
              <div style={{ display: "flex", gap: "12px" }}>
                {[1,2,3,4].map(i => <Skeleton.Button key={i} active style={{ width: "130px", height: "100px", borderRadius: "12px" }} />)}
              </div>
            ) : roles.length === 0 ? (
              <Empty description="No roles found" />
            ) : (
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {roles.map(role => (
                  <RoleCard
                    key={role.id}
                    role={role}
                    selected={selectedRole?.id === role.id}
                    onClick={() => handleSelectRole(role)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Stats row ── */}
          {selectedRole && matrixData && !matrixLoading && (
            <div style={{ display: "flex", gap: "14px", marginBottom: "24px", flexWrap: "wrap" }}>
              {[
                { label: "Menus with Access", value: `${grantedMenus}/${totalMenus}`, color: "#5b48e8", bg: "#f5f3ff", icon: <SafetyCertificateOutlined /> },
                { label: "Permissions Granted", value: totalGranted, color: "#16a34a", bg: "#f0fdf4", icon: <CheckCircleFilled /> },
                { label: "Permissions Restricted", value: (totalMenus * PERMS.length) - totalGranted, color: "#dc2626", bg: "#fef2f2", icon: <CloseCircleFilled /> },
              ].map(s => (
                <div key={s.label} style={{ background: "white", borderRadius: "12px", padding: "14px 20px", border: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: "12px", minWidth: "180px", boxShadow: "0 2px 8px rgba(0,0,0,.04)", flex: 1 }}>
                  <div style={{ width: "36px", height: "36px", background: s.bg, borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", color: s.color, fontSize: "16px" }}>{s.icon}</div>
                  <div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: "#111827", lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500, marginTop: "2px" }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Permission Matrix ── */}
          {selectedRole ? (
            <div style={{ background: "white", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 2px 16px rgba(0,0,0,.07)", overflow: "hidden" }}>
              {/* Matrix Header */}
              <div style={{ display: "grid", gridTemplateColumns: gridCols, padding: "14px 16px", background: "linear-gradient(135deg,#f8f9ff 0%,#f0f4ff 100%)", borderBottom: "2px solid #e8ecff", alignItems: "center" }}>
                <div style={{ fontSize: "12px", fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Menu / Feature
                </div>
                {PERMS.map(p => (
                  <div key={p.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                    <div style={{ width: "28px", height: "28px", borderRadius: "7px", background: p.bg, display: "flex", alignItems: "center", justifyContent: "center", color: p.color, fontSize: "13px" }}>
                      {p.icon}
                    </div>
                    <span style={{ fontSize: "9px", fontWeight: 700, color: p.color, textTransform: "uppercase", letterSpacing: "0.3px" }}>{p.label}</span>
                  </div>
                ))}
              </div>

              {/* Matrix Body */}
              {matrixLoading ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <div className="spinner-border" style={{ color: "#0284c7", width: "32px", height: "32px" }} role="status" />
                  <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "12px" }}>Loading permissions for <strong>{selectedRole.role_name}</strong>...</div>
                </div>
              ) : matrixData?.rows.length === 0 ? (
                <div style={{ padding: "60px 24px", textAlign: "center" }}>
                  <Empty description={<span style={{ color: "#9ca3af" }}>No menus configured in the system</span>} />
                </div>
              ) : (
                <div>
                  {matrixData.rows.map(row => (
                    <div key={row.id}>
                      <MatrixRow name={row.name} icon={row.icon} perms={row.perms} depth={0} />
                      {row.subMenus.map(sub => (
                        <MatrixRow key={sub.id} name={sub.name} icon={sub.icon} perms={sub.perms} depth={1} isSubmenu />
                      ))}
                    </div>
                  ))}

                  {/* Legend footer */}
                  <div style={{ padding: "12px 16px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}>Legend:</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#16a34a", fontWeight: 600 }}>
                      <CheckCircleFilled style={{ fontSize: "13px" }} /> Granted
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#d1d5db", fontWeight: 600 }}>
                      <CloseCircleFilled style={{ fontSize: "13px" }} /> Not granted
                    </div>
                    <span style={{ marginLeft: "auto", fontSize: "11px", color: "#9ca3af" }}>
                      Viewing: <strong style={{ color: "#374151" }}>{selectedRole.role_name}</strong>
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ background: "white", borderRadius: "16px", padding: "60px 24px", border: "1px solid #f0f0f0", textAlign: "center", boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}>
              <LockOutlined style={{ fontSize: "40px", color: "#e5e7eb", marginBottom: "12px" }} />
              <div style={{ fontSize: "15px", fontWeight: 600, color: "#9ca3af" }}>Select a role above to view its permissions</div>
            </div>
          )}

          {/* ── Add / Edit Permission Drawer ── */}
          <Drawer
            title={null}
            placement="right"
            width={420}
            open={permDrawerOpen}
            onClose={() => !permSaving && setPermDrawerOpen(false)}
            closable={false}
            zIndex={1050}
            styles={{ body: { padding: 0 }, header: { display: "none" } }}
          >
            {/* Drawer Header */}
            <div style={{ background: "linear-gradient(135deg,#16a34a,#15803d)", padding: "20px 24px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", background: "rgba(255,255,255,0.15)", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <SafetyCertificateOutlined style={{ color: "white", fontSize: "16px" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "white" }}>{permEditItem ? "Edit Permission" : "Add Permission"}</div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>{permEditItem ? "Update permission details" : "Create a new CMS permission"}</div>
                  </div>
                </div>
                <button onClick={() => !permSaving && setPermDrawerOpen(false)} style={{ width: "30px", height: "30px", borderRadius: "7px", border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>×</button>
              </div>
            </div>

            {/* Drawer Body */}
            <div style={{ padding: "24px" }}>
              {/* Permission Name */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#374151", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Permission Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  value={permForm.permission_name}
                  onChange={e => { setPermForm(f => ({ ...f, permission_name: e.target.value })); if (e.target.value.trim()) setPermFormErr(""); }}
                  placeholder="e.g. Manage Leads"
                  style={{
                    width: "100%", height: "42px", padding: "0 14px", borderRadius: "10px",
                    border: `1.5px solid ${permFormErr ? "#ef4444" : "#e5e7eb"}`,
                    fontSize: "14px", outline: "none", color: "#111827", boxSizing: "border-box",
                    boxShadow: permFormErr ? "0 0 0 3px rgba(239,68,68,0.1)" : "none",
                  }}
                />
                {permFormErr && <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px" }}>{permFormErr}</div>}
              </div>

              {/* Module */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#374151", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Module{" "}<span style={{ fontSize: "11px", fontWeight: 400, color: "#9ca3af", textTransform: "none", letterSpacing: 0 }}>optional</span>
                </label>
                <input
                  value={permForm.module}
                  onChange={e => setPermForm(f => ({ ...f, module: e.target.value }))}
                  placeholder="e.g. leads, orders, users"
                  style={{
                    width: "100%", height: "42px", padding: "0 14px", borderRadius: "10px",
                    border: "1.5px solid #e5e7eb", fontSize: "14px", outline: "none",
                    color: "#111827", boxSizing: "border-box",
                  }}
                />
                <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>Used to group permissions by feature area</div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: "24px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#374151", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Description{" "}<span style={{ fontSize: "11px", fontWeight: 400, color: "#9ca3af", textTransform: "none", letterSpacing: 0 }}>optional</span>
                </label>
                <textarea
                  value={permForm.description}
                  onChange={e => setPermForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of what this permission controls"
                  rows={3}
                  style={{
                    width: "100%", padding: "10px 14px", borderRadius: "10px",
                    border: "1.5px solid #e5e7eb", fontSize: "14px", outline: "none",
                    color: "#111827", resize: "vertical", boxSizing: "border-box",
                  }}
                />
              </div>

              {permEditItem && (
                <div style={{ padding: "10px 14px", background: "#f9fafb", borderRadius: "8px", marginBottom: "20px", fontSize: "12px", color: "#6b7280" }}>
                  <strong>Slug:</strong> <code style={{ background: "#f0f0f0", padding: "1px 6px", borderRadius: "4px" }}>{permEditItem.slug}</code>
                  {" · "}
                  <strong>ID:</strong> {permEditItem.id}
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "16px 24px", borderTop: "1px solid #f3f4f6", background: "white", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => !permSaving && setPermDrawerOpen(false)} disabled={permSaving} style={{ padding: "9px 20px", borderRadius: "9px", border: "1px solid #e5e7eb", background: "white", color: "#374151", fontWeight: 600, fontSize: "13px", cursor: permSaving ? "not-allowed" : "pointer" }}>
                Cancel
              </button>
              <button onClick={handleSavePerm} disabled={permSaving} style={{
                padding: "9px 24px", borderRadius: "9px", border: "none",
                background: permSaving ? "#86efac" : "linear-gradient(135deg,#16a34a,#15803d)",
                color: "white", fontWeight: 700, fontSize: "13px",
                cursor: permSaving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "7px",
              }}>
                {permSaving && <span className="spinner-border spinner-border-sm" role="status" />}
                {permSaving ? "Saving..." : (permEditItem ? "Update" : "Create")}
              </button>
            </div>
          </Drawer>

        </div>
      </div>
    </div>
  );
};

export default ManagePermissions;
